import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  Filter,
  Heading,
  Paginator,
  Spinner,
  StatCard,
  StatCardGroup,
  TextField,
  type FilterOption,
} from '@humanforce/mango-ui-react';
import { NewPayRunModal, type NewPayRunCloseReason } from './NewPayRunModal';
import { useNavigate } from 'react-router';
import { PayRunCard } from './PayRunCard';
import { listBusinessRecordReferences, listPayGroupReferences, listPayRuns } from './payRunsHttpClient';
import type {
  BusinessRecordReference,
  PayGroupReference,
  PayRunListQuery,
  PayRunListResult,
} from './payRunsApiTypes';

const PAGE_SIZE = 20;
const EMPTY_RESULT: PayRunListResult = {
  items: [],
  summary: { pending: 0, compliance: 0, readyToPay: 0 },
  pagination: { page: 1, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 0 },
};

const singleFilterValue = (value: string | number | null): string =>
  typeof value === 'string' ? value : value == null ? '' : String(value);

const toFilterOptions = <T,>(
  values: T[],
  getValue: (value: T) => string,
  getLabel: (value: T) => string,
): FilterOption[] => values.map((value) => ({ value: getValue(value), label: getLabel(value) }));

export const PayRunsScreen = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [businessRecordId, setBusinessRecordId] = useState('');
  const [payGroupId, setPayGroupId] = useState('');
  const [frequency, setFrequency] = useState('');
  const [workflowStatus, setWorkflowStatus] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<PayRunListResult>(EMPTY_RESULT);
  const [businessRecords, setBusinessRecords] = useState<BusinessRecordReference[]>([]);
  const [payGroups, setPayGroups] = useState<PayGroupReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const requestSequence = useRef(0);
  const payGroupRequestSequence = useRef(0);

  const query = useMemo<PayRunListQuery>(() => ({
    scope: 'in_flight',
    search: search || undefined,
    businessRecordId: businessRecordId || undefined,
    payGroupId: payGroupId || undefined,
    workflowStatus: workflowStatus || undefined,
    frequency: frequency || undefined,
    page,
    pageSize: PAGE_SIZE,
    sort: 'payment_date_desc',
  }), [businessRecordId, frequency, page, payGroupId, search, workflowStatus]);

  const load = useCallback(async (signal?: AbortSignal) => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const next = await listPayRuns(query, signal);
      if (sequence === requestSequence.current) setResult(next);
    } catch (loadError) {
      if (signal?.aborted) return;
      if (sequence === requestSequence.current)
        setError(loadError instanceof Error ? loadError.message : 'Pay Runs could not be loaded.');
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    let active = true;
    void listBusinessRecordReferences()
      .then((records) => {
        if (active) setBusinessRecords(records);
      })
      .catch(() => {
        if (active) setBusinessRecords([]);
      });
    return () => { active = false; };
  }, []);

  const chooseBusinessRecord = (value: string | number | null) => {
    const next = singleFilterValue(value);
    setBusinessRecordId(next);
    setPayGroupId('');
    setPayGroups([]);
    setPage(1);
    const sequence = ++payGroupRequestSequence.current;
    if (next) {
      void listPayGroupReferences(next)
        .then((groups) => {
          if (sequence === payGroupRequestSequence.current) setPayGroups(groups);
        })
        .catch(() => {
          if (sequence === payGroupRequestSequence.current) setPayGroups([]);
        });
    }
  };

  const criteriaActive = Boolean(search || businessRecordId || payGroupId || frequency || workflowStatus);
  const clearCriteria = () => {
    setSearch('');
    setBusinessRecordId('');
    setPayGroupId('');
    setPayGroups([]);
    payGroupRequestSequence.current += 1;
    setFrequency('');
    setWorkflowStatus('');
    setPage(1);
  };
  const closeDialog = (reason: NewPayRunCloseReason, createdPayId?: string) => {
    setDialogOpen(false);
    if (reason === 'created' && createdPayId) {
      navigate(`/payroll/pay-runs/${encodeURIComponent(createdPayId)}`);
      return;
    }
    if (reason === 'created') void load();
    requestAnimationFrame(() =>
      document.querySelector<HTMLButtonElement>('#new-pay-run-trigger button')?.focus());
  };

  return (
    <div className="mgo-stack mgo-gap-xl" style={{ padding: 'var(--mgo-space-60)' }}>
      <div className="mgo-split mgo-align-start mgo-gap-l">
        <div className="mgo-stack mgo-gap-xs">
          <Heading level={1}>Pay Runs</Heading>
          <p className="mgo-text-s mgo-color-muted">Review and manage current Pay Run calculations, exceptions and employee totals.</p>
        </div>
        <div id="new-pay-run-trigger">
          <Button label="New Pay Run" icon="add" variant="primary" onClick={() => setDialogOpen(true)} />
        </div>
      </div>

      <section aria-label="Pay Run summary">
        <StatCardGroup>
        {[
          ['Pending Pay Runs', result.summary.pending, 'clock', 'blue'],
          ['Compliance Pay Runs', result.summary.compliance, 'status-caution', 'yellow'],
          ['Ready to pay Pay Runs', result.summary.readyToPay, 'status-positive', 'green'],
        ].map(([label, count, icon, tone], index) => {
          const status = [
            'open',
            'payroll_active',
            'ready_to_pay',
          ][index];
          const selected = workflowStatus === status;
          const numericCount = Number(count);
          return <StatCard key={String(label)} label={String(label)} value={numericCount}
            icon={icon as 'clock'} tone={tone as 'blue' | 'yellow' | 'green'}
            affordance={selected || !workflowStatus ? 'filterVisible' : 'filterHidden'}
            filterActive={selected}
            onClick={() => { setWorkflowStatus(selected ? '' : status); setPage(1); }} />;
        })}
        </StatCardGroup>
      </section>

      <section aria-label="Pay Run filters" className="mgo-stack mgo-gap-m">
        <TextField
          label="Search Pay Runs"
          placeholder="Search by Pay Run, Business Record or Pay Group"
          leadingIcon="search"
          clearable
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          onClear={() => { setSearch(''); setPage(1); }}
        />
        <div className="mgo-cluster mgo-gap-s">
          <Filter
            type="single-select"
            name="Business Record"
            options={toFilterOptions(businessRecords, (record) => record.businessRecordId, (record) => record.displayName)}
            value={businessRecordId || null}
            onChange={chooseBusinessRecord}
          />
          <Filter
            type="single-select"
            name="Pay Group"
            options={toFilterOptions(payGroups, (group) => group.payGroupId, (group) => group.name)}
            value={payGroupId || null}
            disabled={!businessRecordId}
            onChange={(value) => { setPayGroupId(singleFilterValue(value)); setPage(1); }}
          />
          <Filter
            type="single-select"
            name="Frequency"
            options={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'fortnightly', label: 'Fortnightly' },
              { value: 'four_weekly', label: 'Four Weekly' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
            ]}
            value={frequency || null}
            onChange={(value) => { setFrequency(singleFilterValue(value)); setPage(1); }}
          />
          <Filter
            type="single-select"
            name="Workflow Status"
            options={[
              { value: 'open', label: 'Pending' },
              { value: 'payroll_active', label: 'Compliance' },
              { value: 'ready_to_pay', label: 'Ready to Pay' },
            ]}
            value={workflowStatus || null}
            onChange={(value) => { setWorkflowStatus(singleFilterValue(value)); setPage(1); }}
          />
          {criteriaActive && <Button label="Clear filters" icon="filter-clear" variant="tertiary" size="small" onClick={clearCriteria} />}
        </div>
      </section>

      <section aria-label="In-flight Pay Runs" aria-busy={loading} className="mgo-stack mgo-gap-l">
        {error && (
          <Callout
            tone="critical"
            title="Pay Runs unavailable"
            action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}
          >
            {error}
          </Callout>
        )}

        {loading && !error ? (
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l" aria-live="polite">
            {[1, 2].map((placeholder) => (
              <Card key={placeholder}>
                <div className="mgo-stack mgo-gap-l" style={{ minHeight: '10rem' }}>
                  <div className="mgo-cluster mgo-gap-s">
                    <Spinner />
                    <span className="mgo-text-s mgo-color-muted">Loading Pay Runs…</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : !error && result.items.length === 0 ? (
          <div className="mgo-stack mgo-justify-center mgo-align-center mgo-padding-m" style={{ minHeight: '16rem' }}>
            <EmptyState
              variant="no-data"
              title={criteriaActive ? 'No Pay Runs match your filters' : 'No Pay Runs yet'}
              description={criteriaActive
                ? 'Adjust or clear the current filters to see other in-flight Pay Runs.'
                : 'Create the first Standard Pay Run when payroll is ready.'}
              actions={criteriaActive
                ? [<Button key="clear" label="Clear filters" variant="secondary" size="small" onClick={clearCriteria} />]
                : [<Button key="new" label="New Pay Run" variant="primary" size="small" onClick={() => setDialogOpen(true)} />]}
            />
          </div>
        ) : !error ? (
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-grid-cols-desktop-3 mgo-gap-l">
            {result.items.map((payRun) => <PayRunCard key={payRun.payId} payRun={payRun} />)}
          </div>
        ) : null}
      </section>

      {!loading && !error && result.pagination.totalPages > 1 && <Paginator first={(page - 1) * PAGE_SIZE}
        itemsPerPage={PAGE_SIZE} totalRecords={result.pagination.totalItems}
        onPageChange={(event) => setPage(event.page + 1)} />}

      <NewPayRunModal open={dialogOpen} onClose={closeDialog} />
    </div>
  );
};
